import { useCallback, useEffect, useRef, type RefObject } from "react";

/** Keep section dividers a few px under the scroll pane top when jumping. */
const SCROLL_TOP_PADDING = 8;
const SCROLL_SETTLE_FALLBACK_MS = 900;

const getSectionScrollTop = (root: HTMLElement, section: HTMLElement) =>
  section.getBoundingClientRect().top -
  root.getBoundingClientRect().top +
  root.scrollTop;

interface UseMagneticSectionsOptions {
  /** Section keys in render order. */
  sectionKeys: string[];
  activeKey: string;
  /** Off for flat views (e.g. "All" A–Z) — no scroll-spy, no jumps. */
  enabled: boolean;
  /** Bump on chip click to trigger one smooth scroll to `activeKey`. */
  scrollRequestId: number;
  scrollRootRef: RefObject<HTMLElement | null>;
  onActiveChange: (key: string) => void;
  /** When scrolled to the very top, report this key (e.g. "all") instead of the first section. */
  allKey?: string;
}

/**
 * Team Views–style magnetic scroll: a chip click glides its section to the top
 * of the scroll pane, and scrolling highlights whichever section is in view.
 */
const useMagneticSections = ({
  sectionKeys,
  activeKey,
  enabled,
  scrollRequestId,
  scrollRootRef,
  onActiveChange,
  allKey,
}: UseMagneticSectionsOptions) => {
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const scrollingToRef = useRef(false);
  const pendingKeyRef = useRef<string | null>(null);
  const activeKeyRef = useRef(activeKey);
  const lastScrollRequestId = useRef(0);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRafRef = useRef<number | null>(null);
  const listenersCleanup = useRef<(() => void) | null>(null);
  const onActiveChangeRef = useRef(onActiveChange);

  activeKeyRef.current = activeKey;
  onActiveChangeRef.current = onActiveChange;

  useEffect(
    () => () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
      }
      listenersCleanup.current?.();
    },
    []
  );

  const registerSection = useCallback(
    (key: string) => (el: HTMLElement | null) => {
      sectionRefs.current[key] = el;
    },
    []
  );

  const resolveActiveFromScroll = useCallback(() => {
    const root = scrollRootRef.current;
    if (!root || !sectionKeys.length) return null;

    if (allKey && root.scrollTop <= SCROLL_TOP_PADDING) {
      return allKey;
    }

    // Short trailing sections can never reach the top; at the bottom, the last one wins.
    if (
      root.scrollTop > 0 &&
      root.scrollTop + root.clientHeight >= root.scrollHeight - 2
    ) {
      return sectionKeys[sectionKeys.length - 1];
    }

    const anchor = root.scrollTop + SCROLL_TOP_PADDING + 6;
    let current = sectionKeys[0];
    for (const key of sectionKeys) {
      const el = sectionRefs.current[key];
      if (!el) continue;
      if (getSectionScrollTop(root, el) <= anchor) {
        current = key;
      } else {
        break;
      }
    }
    return current;
  }, [allKey, scrollRootRef, sectionKeys]);

  const finishScroll = useCallback(() => {
    scrollingToRef.current = false;
    listenersCleanup.current?.();
    listenersCleanup.current = null;
    const next = pendingKeyRef.current ?? resolveActiveFromScroll();
    pendingKeyRef.current = null;
    if (next && next !== activeKeyRef.current) onActiveChangeRef.current(next);
  }, [resolveActiveFromScroll]);

  const scrollToSection = useCallback(
    (key: string) => {
      const root = scrollRootRef.current;
      const el = sectionRefs.current[key];
      if (!root || !el) return false;

      listenersCleanup.current?.();
      if (settleTimer.current) clearTimeout(settleTimer.current);

      scrollingToRef.current = true;
      pendingKeyRef.current = key;
      root.scrollTo({
        top: Math.max(0, getSectionScrollTop(root, el) - SCROLL_TOP_PADDING),
        behavior: "smooth",
      });

      const eventName = "onscrollend" in window ? "scrollend" : "scroll";
      const onEvent = () => {
        if (settleTimer.current) clearTimeout(settleTimer.current);
        if (eventName === "scrollend") {
          finishScroll();
        } else {
          settleTimer.current = setTimeout(finishScroll, 120);
        }
      };
      root.addEventListener(eventName, onEvent, { passive: true });
      listenersCleanup.current = () =>
        root.removeEventListener(eventName, onEvent);
      settleTimer.current = setTimeout(finishScroll, SCROLL_SETTLE_FALLBACK_MS);
      return true;
    },
    [finishScroll, scrollRootRef]
  );

  // Chip click → wait for the sections to mount (double rAF), retry briefly.
  useEffect(() => {
    if (!scrollRequestId || scrollRequestId === lastScrollRequestId.current) {
      return undefined;
    }
    lastScrollRequestId.current = scrollRequestId;
    if (!enabled || !sectionKeys.includes(activeKey)) return undefined;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const attempt = (triesLeft: number) => {
      if (cancelled) return;
      if (!scrollToSection(activeKey) && triesLeft > 0) {
        retryTimer = setTimeout(() => attempt(triesLeft - 1), 50);
      }
    };
    const outer = requestAnimationFrame(() => {
      requestAnimationFrame(() => attempt(3));
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(outer);
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [scrollRequestId, enabled, activeKey, sectionKeys, scrollToSection]);

  // Scroll-spy: highlight the section currently at the top of the pane.
  useEffect(() => {
    const root = scrollRootRef.current;
    if (!enabled || !root || !sectionKeys.length) return undefined;

    const onScroll = () => {
      if (scrollingToRef.current) return;
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
      }
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;
        const resolved = resolveActiveFromScroll();
        if (resolved && resolved !== activeKeyRef.current) {
          onActiveChangeRef.current(resolved);
        }
      });
    };
    root.addEventListener("scroll", onScroll, { passive: true });
    return () => root.removeEventListener("scroll", onScroll);
  }, [enabled, resolveActiveFromScroll, scrollRootRef, sectionKeys]);

  return registerSection;
};

export default useMagneticSections;
